import React, { useState, useEffect } from "react";
import { updateHostProperty } from "../../store/thunks/hostActions";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPropertyDetails } from "../../store/thunks/hostActions";
import { deleteHostProperty } from "../../store/thunks/hostActions";
import { Alert, CircularProgress } from "@mui/material";
import { updatePropertyReset } from "../../store/slices/hostSlices";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Link } from "react-router-dom";

const getInitialHideAddressState = () => {
  const storedValue = localStorage.getItem("hideAddress");
  return storedValue ? JSON.parse(storedValue) : false;
};

// Modern Property Header Component
function PropertyHeader({ property, onEdit, onDelete, editMode, onSave, onCancel }) {
  return (
    <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-3xl p-8 mb-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/host"
            className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {property?.listingTitle || 'Property Details'}
            </h1>
            <p className="text-primary-100">
              {property?.address?.line1}, {property?.address?.city}, {property?.address?.state}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {editMode ? (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                className="px-6 py-2 bg-white text-primary-600 rounded-xl hover:bg-gray-100 transition-colors font-medium shadow-lg"
              >
                Save Changes
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onDelete}
                className="p-3 bg-red-500/20 text-red-200 rounded-xl hover:bg-red-500/30 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <button
                onClick={onEdit}
                className="px-6 py-3 bg-white text-primary-600 rounded-xl hover:bg-gray-100 transition-colors font-medium shadow-lg"
              >
                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Property
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Modern Property Info Card
function PropertyInfoCard({ title, children, icon }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-2 bg-primary-100 rounded-lg">
          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icon}
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// Modern Input Field
function ModernInput({ label, value, onChange, placeholder, type = "text", readOnly = false, required = false }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`w-full px-4 py-3 border rounded-xl transition-colors ${
          readOnly 
            ? 'bg-gray-50 border-gray-200 text-gray-600' 
            : 'bg-white border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
        }`}
      />
    </div>
  );
}

// Modern Textarea
function ModernTextarea({ label, value, onChange, placeholder, readOnly = false, required = false, rows = 4 }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        rows={rows}
        className={`w-full px-4 py-3 border rounded-xl transition-colors resize-none ${
          readOnly 
            ? 'bg-gray-50 border-gray-200 text-gray-600' 
            : 'bg-white border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
        }`}
      />
    </div>
  );
}

// Modern Checkbox
function ModernCheckbox({ label, checked, onChange, description }) {
  return (
    <div className="flex items-start space-x-3">
      <div className="flex items-center h-5">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
        />
      </div>
      <div className="text-sm">
        <label className="font-medium text-gray-900">{label}</label>
        {description && (
          <p className="text-gray-500 mt-1">{description}</p>
        )}
      </div>
    </div>
  );
}

// Copy Link Component
function CopyLinkSection({ property, copyText, setCopyText }) {
  const unsecuredCopyToClipboard = (text, description) => {
    const textArea = document.createElement("textarea");
    textArea.value = description + " " + text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Unable to copy to clipboard", err);
    }
    document.body.removeChild(textArea);
  };

  return (
    <PropertyInfoCard
      title="Property Links"
      icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />}
    >
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Property URL</label>
          <div className="flex space-x-3">
            <input
              type="text"
              value={property?.uniqueLink || ''}
              readOnly
              className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600"
            />
            <button
              onClick={() => {
                unsecuredCopyToClipboard(property?.uniqueLink, "");
                setCopyText("Copied!");
                setTimeout(() => setCopyText("Copy URL"), 3000);
              }}
              className="px-4 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors font-medium"
            >
              {copyText === "Copied!" ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Share Message</label>
          <div className="flex space-x-3">
            <textarea
              value={`I'm always looking for ways to elevate your stay, and I think you'll love this! We've partnered with Bellhopt to offer affordable pre-arrival grocery delivery. No more initial store runs; let's get your vacation started on the right foot! The best part? No need to create an account. Simply order directly to your rental here: ${property?.uniqueLink || ''}`}
              readOnly
              rows={4}
              className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 resize-none"
            />
            <button
              onClick={() => {
                const description = `I'm always looking for ways to elevate your stay, and I think you'll love this! We've partnered with Bellhopt to offer affordable pre-arrival grocery delivery. No more initial store runs; let's get your vacation started on the right foot! The best part? No need to create an account. Simply order directly to your rental here: `;
                unsecuredCopyToClipboard(property?.uniqueLink, description);
                setCopyText("Copied!");
                setTimeout(() => setCopyText("Copy Message"), 3000);
              }}
              className="px-4 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors font-medium"
            >
              {copyText === "Copied!" ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </PropertyInfoCard>
  );
}

function PropertyDetailsComponent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const [checkInTime, setCheckInTime] = useState();
  const [checkOutTime, setCheckoutTime] = useState();
  const [editMode, setEditMode] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [copyText, setCopyText] = useState("Copy Message");

  const { pid } = params;
  const [queryParam] = useSearchParams();

  // Property Details UseSelector Hook
  const { property: property_d } = useSelector((state) => state.propertyDetails);

  // Update Property UseSelector Hook
  const {
    loading: loadingUpdate,
    success: successUpdate,
    error: errorUpdate,
  } = useSelector((state) => state.propertyUpdate);

  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [credentials, setCredentials] = useState({
    description: "",
    keyInfo: "",
    listingTitle: "",
    deliveryTime: ""
  });

  const [hideAddress, setHideAddress] = useState(getInitialHideAddressState);

  const handleTimeChange = (selectedTime) => {
    setCheckInTime(selectedTime);
  };

  const handleTimeChange2 = (selectedTime) => {
    setCheckoutTime(selectedTime);
  };

  const handleCheckboxChange = () => {
    setHideAddress(!hideAddress);
  };

  // Use useEffect to update localStorage when hideAddress changes
  useEffect(() => {
    localStorage.setItem("hideAddress", JSON.stringify(hideAddress));
  }, [hideAddress]);

  // onClick Handlers
  const handleEditSubmit = () => {
    const propertyCredentials = {
      checkInTime,
      checkOutTime,
      ...credentials,
      address,
      hideAddress,
    };

    console.log(propertyCredentials);
    dispatch(updateHostProperty({ propertyCredentials, pid }));
  };

  const handleDeleteBtn = () => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      dispatch(deleteHostProperty(pid));
      navigate("/host");
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setIsReadOnly(false);
  };

  const handleCancel = () => {
    setEditMode(false);
    setIsReadOnly(true);
    // Reset to original values
    setAddress({ ...property_d.address });
    setCredentials({
      description: property_d.description,
      keyInfo: property_d.keyInfo,
      listingTitle: property_d.listingTitle,
      deliveryTime: property_d.deliveryTime || ""
    });
  };

  useEffect(() => {
    if (queryParam.get("edit")) {
      setEditMode(true);
      setIsReadOnly(false);
    }

    if (successUpdate) {
      setIsReadOnly(true);
      setEditMode(false);
      navigate("/host");
    } else {
      if (!property_d?._id || property_d?._id !== pid) {
        dispatch(getPropertyDetails(pid));
      } else {
        setAddress({ ...property_d.address });
        setCredentials({
          description: property_d.description,
          keyInfo: property_d.keyInfo,
          listingTitle: property_d.listingTitle
        });
      }
    }

    dispatch(getPropertyDetails(pid));
  }, [dispatch, pid, property_d?._id, navigate, successUpdate]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(updatePropertyReset());
    }, 2000);
  }, [errorUpdate]);


  if (loadingUpdate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#EF4949' }} />
          <p className="mt-4 text-gray-600 font-medium">Updating property...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <PropertyHeader
          property={property_d}
          onEdit={handleEdit}
          onDelete={handleDeleteBtn}
          editMode={editMode}
          onSave={handleEditSubmit}
          onCancel={handleCancel}
        />

        {/* Error Alert */}
        {errorUpdate && (
          <Alert severity="error" className="mb-6">
            {errorUpdate}
          </Alert>
        )}

        {/* Success Alert */}
        {successUpdate && (
          <Alert severity="success" className="mb-6">
            Property updated successfully!
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Basic Information */}
            <PropertyInfoCard
              title="Basic Information"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
            >
              <div className="space-y-6">
                <ModernInput
                  label="Property Title"
                  value={credentials.listingTitle}
                  onChange={(e) => setCredentials({ ...credentials, listingTitle: e.target.value })}
                  placeholder="Enter property title"
                  readOnly={isReadOnly}
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ModernInput
                    label="Address Line 1"
                    value={address.line1}
                    onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                    placeholder="Street address"
                    readOnly={isReadOnly}
                    required
                  />
                  <ModernInput
                    label="Address Line 2"
                    value={address.line2}
                    onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                    placeholder="Apartment, suite, etc."
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ModernInput
                    label="City"
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    placeholder="City"
                    readOnly={isReadOnly}
                    required
                  />
                  <ModernInput
                    label="State"
                    value={address.state}
                    onChange={(e) => setAddress({ ...address, state: e.target.value })}
                    placeholder="State"
                    readOnly={isReadOnly}
                    required
                  />
                  <ModernInput
                    label="ZIP Code"
                    value={address.zipCode}
                    onChange={(e) => setAddress({ ...address, zipCode: e.target.value })}
                    placeholder="ZIP Code"
                    readOnly={isReadOnly}
                    required
                  />
                </div>
              </div>
            </PropertyInfoCard>

            {/* Property Details */}
            <PropertyInfoCard
              title="Property Details"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />}
            >
              <div className="space-y-6">
                <ModernTextarea
                  label="Property Description"
                  value={credentials.description}
                  onChange={(e) => setCredentials({ ...credentials, description: e.target.value })}
                  placeholder="Describe your property..."
                  readOnly={isReadOnly}
                  rows={4}
                />

                <ModernTextarea
                  label="Entry Instructions"
                  value={credentials.keyInfo}
                  onChange={(e) => setCredentials({ ...credentials, keyInfo: e.target.value })}
                  placeholder="How guests can access the property..."
                  readOnly={isReadOnly}
                  rows={3}
                />

                {/* Delivery Time Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Time (when house cleaned)
                  </label>
                  <select
                    value={credentials.deliveryTime || ''}
                    onChange={(e) => setCredentials({ ...credentials, deliveryTime: e.target.value })}
                    disabled={isReadOnly}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a time</option>
                    <option value="8:00 AM">8:00 AM</option>
                    <option value="8:30 AM">8:30 AM</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="9:30 AM">9:30 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="10:30 AM">10:30 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="11:30 AM">11:30 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="12:30 PM">12:30 PM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="1:30 PM">1:30 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="2:30 PM">2:30 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="3:30 PM">3:30 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Preferred time for delivery when the house is cleaned.
                  </p>
                </div>

                <ModernCheckbox
                  label="Hide Address from Guests"
                  checked={hideAddress}
                  onChange={handleCheckboxChange}
                  description="When enabled, guests will not see the full address until after booking"
                />
              </div>
            </PropertyInfoCard>

            {/* Check-in/Check-out Times */}
            <PropertyInfoCard
              title="Check-in & Check-out Times"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Check-in Time</label>
                  <DatePicker
                    selected={checkInTime}
                    onChange={handleTimeChange}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    placeholderText="Select check-in time"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Check-out Time</label>
                  <DatePicker
                    selected={checkOutTime}
                    onChange={handleTimeChange2}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    placeholderText="Select check-out time"
                  />
                </div>
              </div>
            </PropertyInfoCard>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Copy Link Section */}
            <CopyLinkSection
              property={property_d}
              copyText={copyText}
              setCopyText={setCopyText}
            />

            {/* Property Stats */}
            <PropertyInfoCard
              title="Property Statistics"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />}
            >
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">Property ID</span>
                  <span className="text-sm font-semibold text-gray-900">{property_d?._id?.slice(-8) || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">Created</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {property_d?.createdAt ? new Date(property_d.createdAt).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">Last Updated</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {property_d?.updatedAt ? new Date(property_d.updatedAt).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </PropertyInfoCard>

            {/* Quick Actions */}
            <PropertyInfoCard
              title="Quick Actions"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />}
            >
              <div className="space-y-3">
                <Link
                  to="/orderList"
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  <span>View Orders</span>
                </Link>
                <Link
                  to="/hostdashboard"
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  <span>View Earnings</span>
                </Link>
              </div>
            </PropertyInfoCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PropertyDetailsComponent;